import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Mail, Phone, Building, ChartBar, Landmark, Target, Focus, Globe, TrendingUp, Briefcase, Users, TextQuote } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import FlywheelDisplay from "@/components/onepager/FlywheelDisplay";
import ReturnsChart from "@/components/onepager/ReturnsChart";
import { LinkedinIcon } from "lucide-react";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import MustangFinancials from "@/components/onepager/MustangFinancials";
import CarBrandLogos from "@/components/onepager/CarBrandLogos";
import DealTimeline from "@/components/onepager/DealTimeline";
import ProjectMustangSection from '@/components/onepager/ProjectMustangSection';
import PDFViewer from '@/components/onepager/PDFViewer';
import AcquisitionStrategySection from "@/components/onepager/AcquisitionStrategySection";
import VisionSection from "@/components/onepager/VisionSection";
import MarketAndPipelineSection from "@/components/onepager/MarketAndPipelineSection";


const NOA_LOGO = "/lovable-uploads/e214b1a5-3e32-46c6-88b4-03c4d3444f26.png";
const TEAM_MEMBERS = [{
  name: "Hussein Fawzi",
  role: "CEO - Partner",
  company: "Noa Partners",
  img: "/lovable-uploads/325dbc11-b85f-4bbd-ac2b-23905415c5de.png",
  background: "Tech founder and operator; built Mexico's #2 largest restaurant VMS via an M&A roll-up."
}, {
  name: "Ronni Samir",
  role: "CIO - Partner",
  company: "Noa Partners",
  img: "/lovable-uploads/062e011a-6054-4e22-9bb8-3ef795f23d6e.png",
  background: "ETA with +10 years experience acquiring and building B2B Saas companies across LatAm."
}, {
  name: "Ignacio Lizarraga",
  role: "Chief Financial Officer",
  company: "Noa Partners",
  img: "/lovable-uploads/d67412b2-4dac-4f70-a82a-3c39286ebeaf.png",
  background: "Investor with 10+ years in M&A, private equity, and search funds across LatAm."
}];

const OnePagerPage: React.FC = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const totalSections = 4;
  const goToNextSection = () => {
    setCurrentSection(prev => (prev + 1) % totalSections);
  };
  const goToPreviousSection = () => {
    setCurrentSection(prev => (prev - 1 + totalSections) % totalSections);
  };

  const CASE_STUDY = {
    vertical: "Restaurant Tech",
    productType: "Restaurant Management Software",
    ceoImage: "/lovable-uploads/325dbc11-b85f-4bbd-ac2b-23905415c5de.png",
    ceoQuote: "Noa Partners has been an invaluable partner in our growth journey. Their strategic guidance and operational expertise have helped us scale our business and achieve our goals.",
    ceoName: "Hussein Fawzi",
    ceoRole: "CEO, Ambit Technology"
  };

  return <div className="bg-white min-h-screen p-4 flex flex-col items-center justify-center">
    <div className="fixed top-4 right-4 flex items-center gap-2 z-50">
      <Button variant="outline" size="icon" onClick={goToPreviousSection} disabled={currentSection === 0} className="bg-white/80 backdrop-blur-sm">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <div className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-md text-sm font-medium">
        {currentSection + 1} / {totalSections}
      </div>
      <Button variant="outline" size="icon" onClick={goToNextSection} disabled={currentSection === totalSections - 1} className="bg-white/80 backdrop-blur-sm">
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>

    <div className="flex flex-col items-center transition-transform duration-500 ease-in-out" style={{
      transform: `translateY(-${currentSection * 100}vh)`
    }}>

      <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-8 overflow-hidden flex flex-col">
        <div className="bg-gradient-to-br from-[#0D1F2D] to-[#1a2c3b] text-white p-12 h-full flex flex-col">
          <div className="flex justify-between items-start mb-16">
            <img src={NOA_LOGO} alt="Noa Partners Logo" className="h-6" />
            <div className="text-right">
              <p className="text-sm text-gray-400 uppercase tracking-wider">Confidential</p>
              <p className="text-sm text-gray-400">2025</p>
            </div>
          </div>

          <div className="flex-grow flex flex-col justify-center w-full">
            <div className="space-y-6 mb-16 w-full">
              <h1 className="text-5xl font-light leading-tight tracking-tight w-full">
                Vertical Market Software <span className="text-blue-400 text-3xl align-super ml-1">(VMS)</span> Acquisition HoldCo.
              </h1>

              <Separator className="my-8 bg-blue-400/20 w-32" />

              <div className="flex flex-col justify-start items-start w-full">
                <div className="border-l-4 border-blue-400 pl-4 space-y-4 w-full">
                  <p className="text-xl text-gray-300 leading-relaxed font-light w-full">Building Latin America's permanent VMS HoldCo—strategically acquiring, preserving, and scaling the region's top vertical software businesses.</p>
                  <p className="text-xl text-gray-300 leading-relaxed font-light w-full">
                    By executing disciplined, deal-by-deal investments to maximize today's risk-adjusted returns and de-risk our unified HoldCo for tomorrow.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-8 mt-auto w-full">
              
            </div>
          </div>

          

          <div className="mt-auto pt-8">
            <Separator className="mb-8 bg-blue-400/20" />
            <div className="flex justify-between items-center text-sm text-gray-400">
              <span>Noa Partners © 2025</span>
              <span>www.noapartners.com</span>
            </div>
          </div>
        </div>
      </section>

      <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-8 overflow-hidden flex flex-col">
        <AcquisitionStrategySection />
      </section>

      <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-8 overflow-hidden flex flex-col">
        <div className="p-12 flex flex-col h-full">
          <div className="mb-8">
            <h2 className="text-4xl font-bold text-[#0D1F2D] mb-4">
              Our <span>Team</span><span className="text-blue-500 ml-1">.</span>
            </h2>
            <div className="border-l-2 border-gray-300 pl-6 py-2 max-w-3xl">
              <p className="text-gray-700 text-lg">Founded by entrepreneurs, operators and investors with a track record in acquiring, building and scaling successful B2B SaaS companies in LatAm.</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-12">
            {TEAM_MEMBERS.map((member, index) => <div key={index} className="bg-white rounded-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-lg">

              {member.img ? <div className="h-44 mb-4 overflow-hidden bg-gray-50">
                <img src={member.img} alt={member.name} className="w-full h-full object-cover" style={{
                  objectPosition: member.name === "Hussein Fawzi" ? "center 15%" : member.name === "Ignacio Lizarraga" ? "center 60%" : "center"
                }} />
              </div> : <div className="h-44 mb-4 flex items-center justify-center bg-gray-50">
                <span>No Image</span>
              </div>}

              <div className="p-4">
                <h3 className="text-xl font-bold text-[#0D1F2D] mb-1">
                  {member.name}
                </h3>
                <p className="text-gray-600 text-sm font-medium mb-3">
                  {member.role}
                </p>
                <p className="text-gray-600 text-sm">
                  {member.background}
                </p>
              </div>
            </div>)}
          </div>

          <div className="mt-6">
            <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 shadow-lg">
              <h3 className="text-xl font-bold text-[#0D1F2D] mb-4">Success Story: Ambit Technology (Acquired 2022)</h3>
              <div className="mb-4">
                <p className="text-sm text-blue-900 font-medium">Vertical: {CASE_STUDY.vertical}</p>
                <p className="text-sm text-blue-900 font-medium">Product: {CASE_STUDY.productType}</p>
              </div>

              <div className="mb-4 bg-blue-50/50 rounded-xl p-3 border border-blue-100">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <img src={CASE_STUDY.ceoImage} alt="Ambit CEO" className="w-14 h-14 rounded-full object-cover border-2 border-blue-200" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <TextQuote className="w-3 h-3 text-blue-500" />
                      <p className="text-sm text-gray-700 italic">
                        {CASE_STUDY.ceoQuote}
                      </p>
                    </div>
                    <p className="text-xs font-semibold text-blue-900">{CASE_STUDY.ceoName}</p>
                    <p className="text-xs text-blue-700">{CASE_STUDY.ceoRole}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-start">
                  <div className="bg-amber-100 p-2 rounded-lg mr-3">
                    <TrendingUp className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#2F4550] mb-1 text-sm">2.5x Growth</h4>
                    <p className="text-[#586F7C] text-xs">More then doubling of revenue and customer base in three years</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-green-100 p-2 rounded-lg mr-3">
                    <Globe className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#2F4550] mb-1 text-sm">Market expansion</h4>
                    <p className="text-[#586F7C] text-xs">
                      Successful entry into 4 new international markets
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3">
                    <ChartBar className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#2F4550] mb-1 text-sm">Strong Returns</h4>
                    <p className="text-[#2F4550] text-xs">
                      Pro-forma IRR: 30% (unrealized, based on fair-market EV/EBITDA multiples)
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-purple-100 p-2 rounded-lg mr-3">
                    <Briefcase className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#2F4550] mb-1 text-sm">4 Tuck-In Acquisitions</h4>
                    <p className="text-[#586F7C] text-xs">4 complementary acquisitions to expand product offering and marketshare</p>
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
        <div className="mt-4 border-t border-gray-200 pt-2">
          <p className="text-[10px] text-gray-500">
            * Unrealized IRR calculated by estimating fair market value based on comparable company valuations and growth metrics.
          </p>
        </div>
      </section>

      <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-8 overflow-hidden flex flex-col">
        <VisionSection />
      </section>

      <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-8 overflow-hidden flex flex-col">
        <div className="bg-gradient-to-br from-[#0D1F2D] to-[#1a2c3b] text-white p-12 h-full flex flex-col">
          <div className="flex justify-between items-start mb-16">
            <img src={NOA_LOGO} alt="Noa Partners Logo" className="h-6" />
            <div className="text-right">
              <p className="text-sm text-gray-400 uppercase tracking-wider">Confidential</p>
              <p className="text-sm text-gray-400">2025</p>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center flex-grow">
            <div className="text-center max-w-2xl">
              <h2 className="text-4xl font-bold text-white mb-6">
                Let's Connect
              </h2>

              <div className="text-xl text-gray-300 mb-12 leading-relaxed">
                Interested in learning more about our approach to acquiring and growing vertical market software companies in Latin America? Let's connect.
              </div>

              <div className="flex justify-center gap-6">
                <a href="mailto:info@noapartners.com" className="flex items-center gap-3 text-lg text-blue-400 hover:text-blue-300 transition-colors">
                  <Mail className="h-6 w-6" />
                  info@noapartners.com
                </a>
              </div>
            </div>
          </div>

          <div className="mt-auto pt-8">
            <Separator className="mb-8 bg-blue-400/20" />
            <div className="flex justify-between items-center text-sm text-gray-400">
              <span>Noa Partners © 2025</span>
              <span>www.noapartners.com</span>
            </div>
          </div>
        </div>
      </section>
    </div>
    <p className="text-noa-charcoal text-lg mb-4 -mt-1 text-center">
      Project Mustang — a Dealership Management Software (DMS) with 33% market share in Mexico's OEM car dealership market, with over 20 years in the industry.
    </p>
  </div>;
};

export default OnePagerPage;
